import React from 'react';
import { Form, FormOnSubmit } from 'components/form';
import {
  SelfOnboardingStepKey,
  useUpdateJobsiteWorkerMutation,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { isNotEmpty } from 'utils';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  SelfOnboardingProfileFormData,
  SelfOnboardingStepProps,
} from 'containers/selfOnboarding/steps/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import {
  StepInfo,
  StepActions,
  StepInfoTitle,
  StepJobsiteInfo,
} from 'containers/selfOnboarding/steps/components';
import { useGetIsSelfOnboardingClosed } from 'containers/selfOnboarding/helpers/useGetIsSelfOnboardingClosed';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import {
  getFormInputsHook,
  getDefaultValues,
  getUpdateInput,
} from './ProfileStep.forms';

export function ProfileStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    updateState,
    navigation,
    stepConfig,
    localize,
    language,
    jobsiteInvitation,
  } = props;
  const {
    worker,
    jobsiteWorker,
    profile,
    contractorId,
    tradeOptions,
    jobTitleOptions,
    unionAffiliationOptions,
    classificationGroupOptions,
    jobsiteCustomWorkerDataFields,
  } = state;
  const {
    goToStep,
    goToNextStep,
    getStepNumberInfo,
    state: navigationState,
  } = navigation;
  const { isReview, autoFocusField } = navigationState;
  const stepNumberInfo = getStepNumberInfo();

  const [isSaving, setIsSaving] = React.useState(false);
  const [updateWorker] = useUpdateWorkerMutation();
  const [updateJobsiteWorker] = useUpdateJobsiteWorkerMutation();
  const { isSelfOnboardingClosed } = useGetIsSelfOnboardingClosed();

  const onSubmit: FormOnSubmit<SelfOnboardingProfileFormData> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      if (await isSelfOnboardingClosed()) {
        goToStep(SelfOnboardingStepKey.Closed);
        return;
      }

      if (!jobsiteWorker) {
        AlertService.alert(
          'danger',
          'Changes cannot be saved!',
          'You must be assigned to the jobsite/contractor to update your profile.',
        );
        setIsSaving(false);
        return;
      }

      if (isNotEmpty(dirtyFields)) {
        const { workerInput, jobsiteWorkerInput } = getUpdateInput(
          data,
          dirtyFields,
          jobsiteCustomWorkerDataFields,
        );
        const promises: Promise<unknown>[] = [];
        if (workerInput)
          promises.push(
            updateWorker({
              variables: {
                workerId: worker.workerId,
                workerInput,
              },
            }),
          );
        // `jobsiteWorker` can be null if the worker onboarded using the default contractor flow,
        // and an admin later changed their contractor to a specific contractor.
        if (jobsiteWorkerInput && jobsiteWorker)
          promises.push(
            updateJobsiteWorker({
              variables: {
                jobsiteWorkerId: jobsiteWorker.jobsiteWorkerId,
                jobsiteWorkerInput,
              },
            }),
          );
        await Promise.all(promises);

        updateState({
          profile: data,
          ...(jobsiteWorker && {
            jobsiteWorker: {
              ...jobsiteWorker,
              customWorkerData: {
                ...jobsiteWorker.customWorkerData,
                ...jobsiteWorkerInput?.customWorkerData,
              },
            },
          }),
        });
      }
      setIsSaving(false);
      goToNextStep();
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  const inputs = getFormInputsHook({
    language,
    tradeOptions,
    jobTitleOptions,
    unionAffiliationOptions,
    classificationGroupOptions,
    jobsiteCustomWorkerDataFields,
  });

  const defaultValues = React.useMemo(
    () =>
      getDefaultValues(
        profile,
        language,
        jobsiteCustomWorkerDataFields,
        jobsiteWorker,
      ),
    [
      profile,
      jobsiteCustomWorkerDataFields,
      jobsiteWorker,
      // , language // do not reset defaultValues `language` changes
    ],
  );

  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={localize(Copy.self_onboarding_profile_header)}
          subtitle={localize(Copy.self_onboarding_profile_instructions)}
          withBottomBorder
          hideSubtitleOnMobile
          stepNumberInfo={stepNumberInfo}
        />
        <StepJobsiteInfo
          jobsiteInvitation={jobsiteInvitation}
          jobsiteWorker={jobsiteWorker}
          contractorName={contractorId?.label}
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        autoFocus={
          (autoFocusField as keyof SelfOnboardingProfileFormData) ?? true
        }
        renderBelow={
          <StepActions
            jobsiteInvitation={jobsiteInvitation}
            localize={localize}
            isReview={isReview}
            actions={isReview ? 'all' : 'continue'}
            onSkip={goToNextStep}
            continueActionWithSpinner={isSaving}
          />
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
